/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import _ from 'lodash';
import {
  IHeaderData,
  IReadOnboardingExcelData
} from '../FileReaderUtils/FileReader';
import { IAny } from '../../../../DashboardKPIWidgets/IEnergyManagementProps';

//
const escapeSpecialChars = (str: IAny) => {
  if (typeof str !== `string`) {
    return str;
  }
  const escapeCharsRegex = /[|\\\[\]^$+*?\.><=!&#<=>=<>=\“\,\'\|]/g;
  // (<=)(>=)(<>)=\&\“\,~!=\'\|#%\(\|\|);
  return str.replace(escapeCharsRegex, '\\$&');
};

export interface IPropertyData {
  propertyKey: string;
  propertyValue: IAny;
  id: string;
  type?: string;
  unit?: string | null;
}

export interface IElementData {
  escapedElementName?: string;
  ElementName: string;
  ElementId: string;
  ElementType: string;
  properties: IPropertyData[];
}

export interface ISpatialElementData {
  SpatialElementName: string;
  SpatialElementId: string;
  properties: IPropertyData[];
}

export interface IAssociationData extends ISpatialElementData {
  associations: IElementData[];
}

export interface IConversionData {
  data: IAssociationData[];
}

export interface IOnboardingPropertyKeys {
  spatialElementPropertyKeys: string[];
  elementPropertyKeys: string[];
}

/**
 * Parses headers and extracts
 * keys of spatial element properties and element properties
 * @param headers
 * @returns spatial element properties and element properties
 * excluding name and ids
 */
const getPropertiesKeys = (headers: IHeaderData): IOnboardingPropertyKeys => {
  const { subHeaders, subHeaderStartIndices } = headers;

  const spatialElementPropertyKeys: string[] = [
    ..._.slice(subHeaders, 0, subHeaderStartIndices[1])
  ];
  const elementPropertyKeys: string[] = [
    ..._.slice(subHeaders, subHeaderStartIndices[1])
  ];

  return { spatialElementPropertyKeys, elementPropertyKeys };
};

/**
 * Returns converted data
 * @param validData Data after header validation
 * @returns
 */
export const convertDataToAssociationSchema = (
  validData: IReadOnboardingExcelData
): IConversionData => {
  const { headers, jsonData } = validData;
  const propertiesKeys: IOnboardingPropertyKeys = getPropertiesKeys(headers);
  const data: IAssociationData[] = [];
  let currSpatialEle: IAssociationData;
  _.forEach(jsonData, (row, i) => {
    const {
      SpatialElementName,
      SpatialElementId,
      ElementName,
      ElementId,
      ElementType
    } = row;
    const excludeKeys = [
      'SpatialElementName',
      'SpatialElementId',
      'ElementName',
      'ElementId',
      'ElementType'
    ];
    if (!_.isEmpty(SpatialElementName) || !_.isEmpty(SpatialElementId)) {
      // This row indicates new spatial ele name, thus we push old data to array
      if (currSpatialEle) {
        data.push(_.cloneDeep<IAssociationData>(currSpatialEle));
      }
      currSpatialEle = {
        SpatialElementName: escapeSpecialChars(SpatialElementName),
        SpatialElementId: SpatialElementId || '',
        properties: [],
        associations: []
      };
      // disabled reading spatial element properties
      // _.forEach(propertiesKeys.spatialElementPropertyKeys, (k: string) => {
      //   if (!_.includes(excludeKeys, k) && row[k] !== '' && !_.isEmpty(k))
      //     currSpatialEle.properties.push({
      //       propertyKey: k,
      //       propertyValue: escapeSpecialChars(row[k]),
      //       id: '',
      //       unit: '',
      //       type: ''
      //     });
      // });
    }
    if (!_.isEmpty(ElementName)) {
      const ele: IElementData = {
        escapedElementName: escapeSpecialChars(ElementName),
        ElementName,
        ElementId: ElementId || '',
        ElementType,
        properties: []
      };
      _.forEach(propertiesKeys.elementPropertyKeys, (k) => {
        if (!_.includes(excludeKeys, k) && row[k] !== '' && !_.isEmpty(k)) {
          ele.properties.push({
            propertyKey: k,
            propertyValue: escapeSpecialChars(row[k]),
            id: '',
            unit: '',
            type: ''
          });
        }
      });
      currSpatialEle.associations.push(ele);
    }
    if (i === jsonData.length - 1) {
      data.push(_.cloneDeep<IAssociationData>(currSpatialEle));
    }
  });
  return { data };
};
